import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import * as yup from 'yup';

import { api, parseError, useToast } from 'common';

import { TextFieldControl } from '../../Control/TextField';

interface AgenticChatRenameDialogProps {
  currentTitle: string;
  isOpen: boolean;
  onClose: () => void;
  onRenamed: () => void;
  sessionId: string;
}

export function AgenticChatRenameDialog(props: AgenticChatRenameDialogProps) {
  const { currentTitle, isOpen, onClose, onRenamed, sessionId } = props;
  const toast = useToast();
  const renameSessionMutation = useMutation({
    mutationFn: async (title: string) =>
      await api.client.AgenticPlatformSession.update({ sessionId, values: { title } }),
  });

  return (
    <Formik<{ title: string }>
      initialValues={{ title: currentTitle }}
      enableReinitialize
      validationSchema={yup.object({
        title: yup
          .string()
          .trim()
          .required('Name is required')
          .max(200, 'Name must be 200 characters or fewer'),
      })}
      onSubmit={async (values) => {
        try {
          await renameSessionMutation.mutateAsync(values.title.trim());
          onRenamed();
          onClose();
        } catch (error) {
          toast.showError(`Error while renaming conversation: ${parseError(error)}`);
        }
      }}
    >
      {({ isSubmitting, resetForm }) => (
        <Dialog
          open={isOpen}
          onClose={() => {
            onClose();
            resetForm();
          }}
          fullWidth
          maxWidth="xs"
          slotProps={{ paper: { component: Form } }}
        >
          <DialogTitle>Rename conversation</DialogTitle>
          <DialogContent>
            <TextFieldControl name="title" label="Name" required autoFocus />
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => {
                onClose();
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" type="submit" loading={isSubmitting}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Formik>
  );
}
